import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { catchError, forkJoin, map, merge, of, switchMap, tap } from 'rxjs';

import { mainPageAction } from './main-page.actions';
import { MainPageNotificationItem } from '../../models/main-page.model';
import { MainPageService } from '../../services/main-page.service';

@Injectable()
export class MainPageEffects {
  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private mainPageService: MainPageService
  ) {}

  mainPageNotificationRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.notificationRequest),
      tap((action) =>
        this.logger.debug('@Effect [Stores] Main Page Notification request ', JSON.stringify(action.notifications))
      ),
      switchMap((action) => {
        const company = action.notifications.filter((f) => f.group === 'company');
        const store = action.notifications.filter((f) => f.group === 'store');

        const company$ = forkJoin(
          company.map((m) =>
            this.mainPageService.getNotification(m.type).pipe(catchError(() => of({ company: { [m.name]: 0 } })))
          )
        );
        const store$ = forkJoin(
          store.map((m) =>
            this.mainPageService.getNotification(m.type).pipe(catchError(() => of({ store: { [m.name]: 0 } })))
          )
        );

        return merge(company$, store$).pipe(
          map((responses) => {
            const merged = responses
              .filter((f) => !!f)
              .reduce((acc, obj) => {
                obj = this.filterObject(obj);
                const key = Object.keys(obj)[0];
                acc[key] = { ...(acc[key] || {}), ...obj[key] };
                return acc;
              }, {} as MainPageNotificationItem);
            return mainPageAction.notificationResponse({ data: merged });
          })
        );
      })
    );
  });

  mainPageSummaryRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.summaryRequest),
      tap((_) => this.logger.debug('@Effect [Stores] Main Page Summary request ')),
      switchMap(() => {
        return this.mainPageService.getSummary().pipe(
          map((response) => {
            return mainPageAction.summaryResponse({ data: response });
          }),
          catchError((err) => of(err))
        );
      })
    );
  });

  mainPageMembershipSummaryRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.membershipSummaryRequest),
      tap((_) => this.logger.debug('@Effect [Stores] Main Page Membership Summary request ')),
      switchMap(() => {
        return this.mainPageService.getMembershipSummary().pipe(
          map((response) => {
            return mainPageAction.membershipSummaryResponse({ data: response });
          }),
          catchError((err) => of(err))
        );
      })
    );
  });

  mainPageShiftScheduleRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.shiftScheduleRequest),
      tap((_) => this.logger.debug('@Effect [Stores] Main Page Shift Schedule request ')),
      switchMap(() => {
        return this.mainPageService.getShiftSchedules().pipe(
          map((response) => {
            return mainPageAction.shiftScheduleResponse({ data: response });
          }),
          catchError(() => of(mainPageAction.shiftScheduleResponse({ data: [] })))
        );
      })
    );
  });

  mainPageDashboardOverviewRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.dashboardOverviewRequest),
      tap((_) => this.logger.debug('@Effect [Stores] Main Page Dashboard Overview request ')),
      switchMap(() => {
        return this.mainPageService.getDashboardOverview().pipe(
          map((response) => mainPageAction.dashboardOverviewResponse({ data: response })),
          catchError((err) => of(err))
        );
      })
    );
  });

  urgentAnnouncementRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(mainPageAction.urgentAnnouncementRequest),
      switchMap(() => {
        return this.mainPageService.getUrgentAnnouncement().pipe(
          map((response) => mainPageAction.urgentAnnouncementResponse({ data: response })),
          catchError(() => of(mainPageAction.urgentAnnouncementResponse({ data: null })))
        );
      })
    );
  });

  filterObject(obj: Record<string, any>) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== null) // ตัด null ทิ้ง
        .map(([key, value]) => {
          if (typeof value === 'object' && !Array.isArray(value)) {
            return [key, this.filterObject(value)];
          }
          return [key, value];
        })
        .filter(([_, value]) => {
          if (typeof value === 'number') {
            return value >= 0;
          }
          if (typeof value === 'object') {
            return Object.keys(value).length > 0;
          }
          return true;
        })
    );
  }
}
